import { useFetchHeaderNavItems } from "@circle-react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";
import { IconButton as CompassIconButton } from "@circleco/compass/components/IconButton";
import { Tooltip as CompassTooltip } from "@circleco/compass/components/Tooltip";
import { Typography as CompassTypography } from "@circleco/compass/components/Typography";
import { useCompassContext } from "@circleco/compass/context/CompassContext";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { t } from "@/i18n-js/instance";
import {
  LOGGED_IN,
  LOGGED_OUT,
} from "@/react/components/SettingsApp/Navigation/AddLink/constants";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { HEADER_V3_ROUTES } from "../constants";

const I18N_ROOT = "settings.navigation.admin_only_route_floating_bar";

export const AdminOnlyRouteNotice = () => {
  const { currentCommunity, currentCommunityMember } = usePunditUserContext();
  const portalContainer = useCompassContext()?.portalContainer;
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const navigate = useNavigate();
  const pathname = useLocation({ select: location => location.pathname });

  const audience = !currentCommunityMember ? LOGGED_OUT : LOGGED_IN;
  const { data: headerNavItems, isInitialLoading: isLoading } =
    useFetchHeaderNavItems({
      id: currentCommunity?.id,
      audience,
    });

  const { isAdminOnlyRoute, matchedLinkId } = useMemo(() => {
    if (!headerNavItems) return { isAdminOnlyRoute: false, matchedLinkId: 0 };

    const matchedItem = headerNavItems.find(item => {
      if (item.visibility !== "admin_only") return false;
      if (item.kind === "custom") return item.url === pathname;
      return HEADER_V3_ROUTES[item.kind] === pathname;
    });

    return {
      isAdminOnlyRoute: !!matchedItem,
      matchedLinkId: matchedItem ? matchedItem.id : null,
    };
  }, [headerNavItems, pathname]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (isAdminOnlyRoute) {
      timer = setTimeout(() => setIsVisible(true), 100);
    } else {
      setIsVisible(false);
      setIsDismissed(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAdminOnlyRoute]);

  const handleNavigateToSettings = useCallback(() => {
    void navigate({
      to: "/settings/navigation",
      search: {
        selectedLinkId: matchedLinkId ?? undefined,
      },
    });
  }, [navigate, matchedLinkId]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setIsDismissed(true);
  }, []);

  if (
    !isAdminOnlyRoute ||
    isLoading ||
    isDismissed ||
    !isCommunityAdmin(currentCommunityMember)
  )
    return null;

  if (!portalContainer) return null;

  const editLabel = t([I18N_ROOT, "cta_text"]);
  const dismissLabel = t([I18N_ROOT, "dismiss"]);

  return createPortal(
    <output
      data-testid="admin-only-route-notice"
      className={classNames(
        "pointer-events-none absolute bottom-16 left-1/2 flex w-max max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-2 md:bottom-8",
        "rounded-lg border border-primary bg-primary px-3 py-2 shadow-md",
        "transition-all duration-150 ease-out",
        {
          "translate-y-0 opacity-100": isVisible,
          "translate-y-2 opacity-0": !isVisible,
        },
      )}
    >
      <CompassIcon
        name="circle-info-filled"
        color="secondary"
        size="md"
        className="shrink-0"
      />
      <CompassTypography
        variant="body-sm"
        color="primary"
        className="min-w-0 flex-1"
      >
        {t([I18N_ROOT, "description"])}
      </CompassTypography>
      <div className="pointer-events-auto ml-auto flex shrink-0 items-center gap-1">
        <CompassTooltip content={editLabel}>
          <CompassIconButton
            icon="pencil"
            variant="ghost"
            size="sm"
            aria-label={editLabel}
            onClick={handleNavigateToSettings}
          />
        </CompassTooltip>
        <CompassTooltip content={dismissLabel}>
          <CompassIconButton
            icon="cross"
            variant="ghost"
            size="sm"
            aria-label={dismissLabel}
            onClick={handleDismiss}
          />
        </CompassTooltip>
      </div>
    </output>,
    portalContainer,
  );
};
